import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthStateService } from '../auth-state/auth-state-service.service';

/**
 * Estado de las alertas de filtros
 */
export interface AlertFilterState {
  showAlertFilterEst: boolean;
  showAlertFilterEst_start_date: string;
  showAlertFilterEst_end_date: string;
  showAlertFilterEst_state: string;
  showAlertFilter: boolean;
  showCountFilter: number;
  showCountFilterEst: number;
  showAlertFilterAll: boolean;
  showAlertCancelled: boolean;
  data_est: boolean;
}

/**
 * Servicio para gestionar las alertas y mensajes de filtros en el módulo de órdenes
 * 
 * FASE C1: Centraliza la lógica de alertas que antes estaba dispersa en OrdersComponent
 * - Gestiona el estado de las alertas de filtros
 * - Genera mensajes dinámicos con traducciones
 * - Maneja alertas por fecha de etapas
 */
@Injectable({
  providedIn: 'root'
})
export class OrdersAlertService {
  
  private alertState = new BehaviorSubject<AlertFilterState>({
    showAlertFilterEst: false,
    showAlertFilterEst_start_date: '',
    showAlertFilterEst_end_date: '',
    showAlertFilterEst_state: '',
    showAlertFilter: false,
    showCountFilter: 0,
    showCountFilterEst: 0,
    showAlertFilterAll: false,
    showAlertCancelled: false,
    data_est: false
  });

  /**
   * Observable del estado de alertas
   */
  public alertState$: Observable<AlertFilterState> = this.alertState.asObservable();

  constructor(
    private translate: TranslateService,
    private authStateService: AuthStateService
  ) {}

  /**
   * Devuelve la URL de "todas las operaciones" según el rol del usuario.
   * Sales no tiene acceso a /orders/all → usa /orders/all-contracts.
   */
  private getAllOrdersRoute(): string {
    const role = this.authStateService.getRole();
    return role === 'Sales' ? '/orders/all-contracts' : '/orders/all';
  }

  /**
   * Actualiza el estado de alertas basado en resultados de búsqueda
   * Migrado de showAlertResult() en OrdersComponent
   * 
   * @param count - Número de resultados encontrados
   * @param searchParams - Parámetros de búsqueda actuales
   * @param typeOrders - Tipo de órdenes actual
   * @param showAlertFilterAll - Flag para mostrar sugerencia de expandir
   * @param showAlertCancelled - Flag para mostrar sugerencia de cancelados
   */
  updateAlertState(
    count: number,
    searchParams: any,
    typeOrders: string,
    showAlertFilterAll: boolean,
    showAlertCancelled: boolean
  ): void {
    const newState: AlertFilterState = {
      ...this.alertState.value,
      showCountFilter: count,
      showAlertFilterAll: showAlertFilterAll,
      showAlertCancelled: showAlertCancelled
    };

    // Verificar filtros por fecha de etapas
    if (Object.keys(searchParams).includes('sent_date[between][]')) {
      newState.showCountFilterEst = count;
      newState.showAlertFilterEst = true;
      newState.showAlertFilterEst_start_date = searchParams['sent_date[between][]'][0];
      newState.showAlertFilterEst_end_date = searchParams['sent_date[between][]'][1];
      newState.showAlertFilterEst_state = 'sent';
      newState.data_est = true;
    } else if (Object.keys(searchParams).includes('risk_approved_date[between][]')) {
      newState.showCountFilterEst = count;
      newState.showAlertFilterEst = true;
      newState.showAlertFilterEst_start_date = searchParams['risk_approved_date[between][]'][0];
      newState.showAlertFilterEst_end_date = searchParams['risk_approved_date[between][]'][1];
      newState.showAlertFilterEst_state = 'risk_approved';
      newState.data_est = true;
    } else if (Object.keys(searchParams).includes('contract_signed_date[between][]')) {
      newState.showCountFilterEst = count;
      newState.showAlertFilterEst = true;
      newState.showAlertFilterEst_start_date = searchParams['contract_signed_date[between][]'][0];
      newState.showAlertFilterEst_end_date = searchParams['contract_signed_date[between][]'][1];
      newState.showAlertFilterEst_state = 'contract_signed';
      newState.data_est = true;
    } else if (Object.keys(searchParams).includes('vehicle_delivered_date[between][]')) {
      newState.showCountFilterEst = count;
      newState.showAlertFilterEst = true;
      newState.showAlertFilterEst_start_date = searchParams['vehicle_delivered_date[between][]'][0];
      newState.showAlertFilterEst_end_date = searchParams['vehicle_delivered_date[between][]'][1];
      newState.showAlertFilterEst_state = 'vehicle_delivered';
      newState.data_est = true;
    } else {
      // Reset estado de etapas si no hay filtro por fecha
      newState.showAlertFilterEst = false;
      newState.showCountFilterEst = 0;
      newState.showAlertFilterEst_start_date = '';
      newState.showAlertFilterEst_end_date = '';
      newState.showAlertFilterEst_state = '';
      newState.data_est = false;
    }

    // Determinar si mostrar alerta de filtro general
    if (!newState.showAlertFilterEst) {
      if (Object.keys(searchParams).length === 0) {
        newState.showAlertFilter = false;
      } else {
        newState.showAlertFilter = true;
      }
    }

    this.alertState.next(newState);
  }

  /**
   * Genera mensaje HTML para alerta de filtro por fecha de etapa
   * Migrado de getEstimatedFilterMessage() en OrdersComponent
   * 
   * @returns Mensaje HTML con interpolación de traducciones
   */
  getEstimatedFilterMessage(): string {
    const state = this.alertState.value;
    
    const key = state.showCountFilterEst === 1 
      ? 'filter.results_stage_singular' 
      : 'filter.results_stage_plural';
    
    const stageText = this.translate.instant(`badge-status_select-stage.${state.showAlertFilterEst_state}`);
    
    return this.translate.instant(key, {
      count: state.showCountFilterEst,
      stage: stageText,
      startDate: state.showAlertFilterEst_start_date,
      endDate: state.showAlertFilterEst_end_date
    });
  }

  /**
   * Genera mensaje HTML para alerta de filtro general
   * Migrado de getGeneralFilterMessage() en OrdersComponent
   * 
   * @param typeOrders - Tipo de órdenes actual para sugerencias contextuales
   * @returns Mensaje HTML con interpolación de traducciones
   */
  getGeneralFilterMessage(typeOrders: string): string {
    const state = this.alertState.value;
    
    if (state.showCountFilter > 0) {
      // HAY resultados
      const key = state.showCountFilter === 1 
        ? 'filter.results_found_singular' 
        : 'filter.results_found_plural';
      
      let message = this.translate.instant(key, { count: state.showCountFilter });
      
      if (state.showAlertFilterAll) {
        message += ' ' + this.buildExpandAllSuggestion();
      }

      return message;
    } else {
      // NO hay resultados
      let message = this.translate.instant('filter.results_not_found');

      // Agregar sugerencias según el contexto
      if (typeOrders === 'cancelled') {
        message += ' ' + this.translate.instant('filter.suggest_frozen');
      } else if (typeOrders === 'frozen') {
        message += ' ' + this.translate.instant('filter.suggest_cancelled');
      } else if (state.showAlertFilterAll) {
        message += ' ' + this.buildExpandAllSuggestion();
      }

      if (state.showAlertCancelled) {
        message += ' ' + this.translate.instant('filter.suggest_cancelled');
      }
      
      return message;
    }
  }

  /**
   * Construye la sugerencia "ampliar a todas las operaciones" con la URL correcta según rol.
   */
  private buildExpandAllSuggestion(): string {
    const route = this.getAllOrdersRoute();
    const linkText = this.translate.instant('filter.click_here');
    return this.translate.instant('filter.suggest_expand_all_dynamic', { route, linkText })
      || `Puedes repetirla ampliándola a todas las Operaciones pulsando <strong><a href='${route}'>${linkText}</a></strong>.`;
  }

  /**
   * Resetea el estado de alertas a valores por defecto
   */
  reset(): void {
    this.alertState.next({
      showAlertFilterEst: false,
      showAlertFilterEst_start_date: '',
      showAlertFilterEst_end_date: '',
      showAlertFilterEst_state: '',
      showAlertFilter: false,
      showCountFilter: 0,
      showCountFilterEst: 0,
      showAlertFilterAll: false,
      showAlertCancelled: false,
      data_est: false
    });
  }

  /**
   * Obtiene el estado actual de forma síncrona
   */
  get currentState(): AlertFilterState {
    return this.alertState.value;
  }

  /**
   * Verifica si hay una alerta de filtro activa
   */
  hasActiveAlert(): boolean {
    const state = this.alertState.value;
    return state.showAlertFilter || state.showAlertFilterEst;
  }

  /**
   * Obtiene el número de resultados actuales
   */
  getCurrentCount(): number {
    return this.alertState.value.showCountFilter;
  }
}
